import styled from '@emotion/styled'
import { Button, Progress } from 'antd'
import BigNumber from 'bignumber.js'
import clsx from 'clsx'
import Numeral from 'numeral'
import { useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useAccount } from 'wagmi'

import failedIcon from '@/assets/failed.png'
import quittedIcon from '@/assets/quitted.png'
import quittingIcon from '@/assets/quitting.png'
import selectedIcon from '@/assets/selected.png'
import selectingIcon from '@/assets/selecting.png'
import unselectedIcon from '@/assets/unselected.png'
import workingIcon from '@/assets/working.png'
import { CardContent } from '@/components/BridgeCard'
import { Icon, IconContainer } from '@/components/BridgeCard/BridgeCard.module'
import DataLoading from '@/components/DataLoading'
import EmptyData from '@/components/EmptyData'
import { PosName } from '@/components/PosCard/PosCard.module'
import { INIT_GROUPID, OSM_STATUS } from '@/config'
import { useLocalStorage } from '@/context/localstorage-context'
import BridgeStatisticsModule from '@/pages/Bridge/bridge-statistics'
import { StakeBridgeContainer } from '@/pages/Bridge/Bridge.module'
import type { StoremanStakeInfoType } from '@/types/storeman-type'
import { cutString, fromWei, fromWei1, ImgCreator } from '@/utils'
import { useQueryVldAttr } from '@/utils/queries'
import { useIwan } from '@/utils/useWebSocket'

import ClaimModal from './claim-modal'
import ExitModal from './exit-modal'
import TopupModal from './topup-modal'

export default function StakeBridge() {
  const { theme } = useLocalStorage()
  const { address } = useAccount()
  const [showExitModal, setShowExitModal] = useState(false)
  const [showTopUpModal, setShowTopUpModal] = useState(false)
  const [showClaimModal, setShowClaimModal] = useState(false)

  const isDark = useMemo(() => theme === 'dark', [theme])
  const { data: storemanConf } = useIwan(['getStoremanConf'])
  const { data: storemanStakeInfo, isLoading: storemanStakeInfoLoading } =
    useIwan<StoremanStakeInfoType[]>(
      ['getStoremanStakeInfo'],
      {
        address,
      },
      !!address,
    )

  const { data: vld_attr_data } = useQueryVldAttr(
    storemanStakeInfo && storemanStakeInfo[0]
      ? storemanStakeInfo[0].wkAddr
      : '',
  )

  const vld_attr = useMemo(() => {
    return vld_attr_data
      ? vld_attr_data
      : storemanStakeInfo && storemanStakeInfo[0]
        ? {
            name: storemanStakeInfo[0].wkAddr,
            iconType: 'png',
            iconData: '',
          }
        : { name: '', iconType: 'png', iconData: '' }
  }, [vld_attr_data, storemanStakeInfo])

  const storemanStakeInfoWkAddr = useMemo(() => {
    return storemanStakeInfo && storemanStakeInfo.length > 0
      ? storemanStakeInfo[0].wkAddr.toLowerCase()
      : ''
  }, [storemanStakeInfo])

  const { data: getStoremanInfo, isLoading: isGetStoremanInfoLoading } =
    useIwan(
      ['getStoremanInfo'],
      {
        wkAddr: storemanStakeInfoWkAddr,
      },
      !!storemanStakeInfoWkAddr,
    )

  const { data: storemanGroupList, isLoading: isStoremanGroupListLoading } =
    useIwan(
      ['getStoremanGroupList'],
      {},
      !!(storemanStakeInfo && storemanStakeInfo.length > 0),
    )

  const {
    data: missingGroupList,
    isLoading: isGetMultiStoremanGroupInfoLoading,
  } = useIwan(
    ['getMultiStoremanGroupInfo'],
    {
      groupId: [
        storemanStakeInfo && storemanStakeInfo[0]
          ? storemanStakeInfo[0].groupId
          : '',
      ],
    },
    !!(storemanStakeInfo && storemanStakeInfo.length !== 0),
  )

  const nextGroupId = useMemo(() => {
    if (
      storemanStakeInfo &&
      storemanStakeInfo.length !== 0 &&
      storemanStakeInfo[0].nextGroupId !== INIT_GROUPID
    ) {
      return storemanStakeInfo[0].nextGroupId
    } else {
      return ''
    }
  }, [storemanStakeInfo])

  const { data: selectedStoremanInfo } = useIwan<Array<string>>(
    ['getSelectedStoreman'],
    {
      groupId: nextGroupId,
    },
    !!nextGroupId,
  )

  const groupInfo = useMemo(() => {
    if (
      storemanStakeInfo &&
      storemanStakeInfo.length !== 0 &&
      storemanGroupList &&
      storemanGroupList.length !== 0 &&
      missingGroupList &&
      missingGroupList.length !== 0
    ) {
      let groupInfo = storemanGroupList.find(
        (v: any) => v.groupId === storemanStakeInfo[0].groupId,
      )
      if (!groupInfo) {
        groupInfo = missingGroupList.find(
          (v: any) => v.groupId === storemanStakeInfo[0].groupId,
        )
      }
      return groupInfo
    } else {
      return null
    }
  }, [storemanGroupList, storemanStakeInfo, missingGroupList])

  const nextGroupInfo = useMemo(() => {
    if (
      storemanStakeInfo &&
      storemanStakeInfo.length !== 0 &&
      storemanGroupList &&
      storemanGroupList.length !== 0
    ) {
      return storemanGroupList.find(
        (v: any) => v.groupId === storemanStakeInfo[0].nextGroupId,
      )
    } else {
      return null
    }
  }, [storemanGroupList, missingGroupList])

  const rank = useMemo(() => {
    if (storemanStakeInfo && storemanStakeInfo.length !== 0) {
      const item = storemanStakeInfo[0]
      if (
        item.nextGroupId !== INIT_GROUPID &&
        selectedStoremanInfo &&
        nextGroupInfo
      ) {
        const index = selectedStoremanInfo.findIndex(
          (v: string) => v.toLowerCase() === item.wkAddr.toLowerCase(),
        )
        return [index === -1 ? index : index + 1, selectedStoremanInfo.length]
      } else {
        return [item.rank, item.selectedCount]
      }
    } else {
      return [undefined, '--']
    }
  }, [storemanStakeInfo, selectedStoremanInfo, nextGroupInfo])

  const status = useMemo(() => {
    if (groupInfo && storemanStakeInfo && storemanStakeInfo.length !== 0) {
      const item = storemanStakeInfo[0]
      let storemanStatus = OSM_STATUS[groupInfo.status]
      if (item.nextGroupId !== INIT_GROUPID && nextGroupInfo) {
        storemanStatus = OSM_STATUS[nextGroupInfo.status]
      }
      if (storemanStatus !== 'Selecting' && rank[0] === undefined) {
        storemanStatus = 'Unselected'
      }
      return storemanStatus
    } else {
      return '--'
    }
  }, [groupInfo, storemanStakeInfo, nextGroupInfo, rank])

  const statusIcon = useMemo(() => {
    switch (status) {
      case 'Ready':
        return workingIcon
      case 'Selecting':
        return selectingIcon
      case 'Selected':
        return selectedIcon
      case 'Failed':
        return failedIcon
      case 'Quitting':
        return quittingIcon
      case 'Quitted':
        return quittedIcon
      default:
        return unselectedIcon
    }
  }, [status])

  const quota = useMemo(() => {
    if (
      storemanStakeInfo &&
      storemanStakeInfo.length > 0 &&
      storemanConf &&
      getStoremanInfo
    ) {
      const data = new BigNumber(fromWei1(storemanStakeInfo[0].deposit))
        .plus(fromWei1(storemanStakeInfo[0].partnerDeposit || '0'))
        .multipliedBy(storemanConf.delegationMulti || '1')
        .minus(fromWei1(getStoremanInfo.delegateDeposit || '0'))
      return data.gt('0') ? Numeral(data).value() : '0'
    } else {
      return null
    }
  }, [storemanStakeInfo, storemanConf, getStoremanInfo])

  const totalDeposit = useMemo(() => {
    if (storemanStakeInfo && storemanStakeInfo.length > 0 && getStoremanInfo) {
      const selfDeposit = new BigNumber(storemanStakeInfo[0].deposit).plus(
        storemanStakeInfo[0].partnerDeposit,
      )
      return fromWei(
        new BigNumber(selfDeposit)
          .plus(getStoremanInfo.delegateInDeposit || '0')
          .toString(10),
      )
    } else {
      return '0'
    }
  }, [storemanStakeInfo, getStoremanInfo])

  const percent = useMemo(() => {
    if (!storemanStakeInfo || !storemanStakeInfo[0] || !quota) return 0

    const selfDeposit = new BigNumber(storemanStakeInfo[0].deposit).plus(
      storemanStakeInfo[0].partnerDeposit,
    )

    const per = new BigNumber(quota)
      .dividedBy(fromWei1(selfDeposit.toString(10)))
      .multipliedBy(10)
      .toNumber()

    return Math.max(0, Math.min(100, Math.round(per)))
  }, [quota, storemanStakeInfo])

  const activity = useMemo(() => {
    if (
      !storemanStakeInfo ||
      !storemanStakeInfo[0] ||
      !storemanStakeInfo[0].activity
    )
      return '0%'
    return (
      new BigNumber(storemanStakeInfo[0].activity)
        .multipliedBy(100)
        .toFixed(2) + '%'
    )
  }, [storemanStakeInfo])

  const selfDeposit = useMemo(() => {
    if (storemanStakeInfo && storemanStakeInfo.length > 0 && getStoremanInfo) {
      const selfDeposit = new BigNumber(storemanStakeInfo[0].deposit).plus(
        storemanStakeInfo[0].partnerDeposit,
      )
      return fromWei(new BigNumber(selfDeposit).toString(10))
    } else {
      return '0'
    }
  }, [storemanStakeInfo, getStoremanInfo])

  const unclaimedData = useMemo(() => {
    if (storemanStakeInfo && storemanStakeInfo.length > 0) {
      const item = storemanStakeInfo[0]
      const ret = item.canStakeClaim
        ? new BigNumber(item.incentive).plus(item.deposit).toString(10)
        : item.incentive
      return fromWei(ret)
    } else {
      return '0'
    }
  }, [storemanStakeInfo])

  const delegateFee = useMemo(() => {
    if (groupInfo) {
      return groupInfo.endTime.toString() === '0'
        ? 'N/A'
        : groupInfo.delegateFee / 100 + '%'
    } else {
      return '0'
    }
  }, [groupInfo])

  const isEmpty = useMemo(
    () => !address || !storemanStakeInfo || storemanStakeInfo.length === 0,
    [storemanStakeInfo, address],
  )

  const isLoading = useMemo(() => {
    const hasValidStakeInfo =
      !!storemanStakeInfo && storemanStakeInfo.length > 0
    const isAdditionalLoading =
      isGetStoremanInfoLoading ||
      isStoremanGroupListLoading ||
      isGetMultiStoremanGroupInfoLoading

    return (
      !!address &&
      (storemanStakeInfoLoading || (hasValidStakeInfo && isAdditionalLoading))
    )
  }, [
    storemanStakeInfoLoading,
    address,
    storemanStakeInfo,
    isGetStoremanInfoLoading,
    isStoremanGroupListLoading,
    isGetMultiStoremanGroupInfoLoading,
  ])

  const isReady = useMemo(() => {
    return ['unselected', 'quitting', 'quitted', 'failed', '--'].includes(
      status,
    )
  }, [status])

  const canExit = useMemo(() => {
    if (storemanStakeInfo && storemanStakeInfo.length > 0) {
      const item = storemanStakeInfo[0]
      return item.canStakeOut && !item.quited
    } else {
      return true
    }
  }, [storemanStakeInfo])

  return (
    <StakeBridgeContainer>
      <BridgeStatisticsModule />
      <div className="bg-white rounded-2xl flex flex-col gap-2 items-start p-[20px] dark:bg-dark-222 min-h-[100px]">
        <div className="text-[20px] font-normal text-light-0 mb-4 flex gap-4 dark:text-white">
          <div>My Bridge Validator Node</div>
        </div>
        <DataLoading loading={isLoading}>
          {!isEmpty ? (
            <div className="w-full">
              <div className="flex gap-6 md:flex-col">
                <div className="flex flex-col w-[65%] border-1 border-light-555 rounded-[14px] text-left dark:border-dark-666 md:w-full">
                  <div className="flex gap-2 text-left p-4">
                    {!!vld_attr.iconData && (
                      <IconContainer style={{ marginTop: '4px' }}>
                        <Icon
                          src={ImgCreator(vld_attr.iconData, vld_attr.iconType)}
                          alt={vld_attr.name}
                        />
                      </IconContainer>
                    )}
                    <PosName className="h-[36px] flex flex-col text-light-0 dark:text-white md:h-full">
                      <span>
                        {vld_attr?.name?.startsWith('0x')
                          ? cutString(vld_attr.name, 8)
                          : vld_attr?.name || 'Unknown'}
                      </span>
                      <span className="dark:text-dark-0">
                        {storemanStakeInfo?.[0]?.wkAddr
                          ? isMobile
                            ? cutString(storemanStakeInfo?.[0]?.wkAddr, 8)
                            : storemanStakeInfo?.[0]?.wkAddr
                          : 'Unknown'}
                      </span>
                    </PosName>
                    <div className="rounded-lg bg-light-102 text-[12px] px-2 gap-2 flex items-center justify-center text-light-103 dark:bg-dark-103">
                      <img
                        src={statusIcon}
                        alt="working"
                        className="w-[14px] h-[14px]"
                      />
                      {status}
                    </div>
                  </div>
                  <div className="w-full text-center p-6">
                    <Progress
                      width={160}
                      type="circle"
                      trailColor={isDark ? '#122A3B' : '#F2F2F2'}
                      strokeWidth={5}
                      strokeColor={isDark ? '#0f68aa' : '#0f68aa'}
                      percent={percent}
                      format={() => (
                        <CardContent
                          storeman={{
                            quota,
                            totalDeposit,
                          }}
                        />
                      )}
                    />
                  </div>
                  <div className="flex border-t-1 border-light-555 p-4 dark:border-dark-666 md:flex-col">
                    <div className="flex-1 flex flex-col gap-2 border-r-1 border-light-555 dark:border-dark-666 md:border-r-0 md:border-b-1">
                      <Title isDark={isDark}>My Stake</Title>
                      <div
                        style={{
                          color: 'rgba(51, 51, 51, 0.5)',
                          fontSize: '12px',
                        }}
                      >
                        <Data isDark={isDark}>
                          {fromWei(storemanStakeInfo?.[0]?.deposit || '0')}
                        </Data>
                        <span className="pl-2 text-light-444">WAN</span>
                      </div>
                    </div>
                    <div className="flex-1 flex flex-col gap-2 border-r-1 border-light-555 pl-[10px] dark:border-dark-666 md:pl-0 md:border-r-0 md:border-b-1 md:pt-[10px]">
                      <Title isDark={isDark}>My Rewards</Title>
                      <div
                        style={{
                          color: 'rgba(51, 51, 51, 0.5)',
                          fontSize: '12px',
                        }}
                      >
                        <Data isDark={isDark}>
                          {fromWei(storemanStakeInfo?.[0]?.incentive || '0')}
                        </Data>
                        <span className="pl-2 text-light-444">WAN</span>
                      </div>
                    </div>
                    <div className="flex-1 flex flex-col gap-2 pl-[10px] md:pl-0 md:pt-[10px]">
                      <Title isDark={isDark}>Claimable Amount</Title>
                      <div
                        style={{
                          color: 'rgba(51, 51, 51, 0.5)',
                          fontSize: '12px',
                        }}
                      >
                        <Data isDark={isDark}>{unclaimedData}</Data>
                        <span className="pl-2 text-light-444">WAN</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex-1 border-1 border-light-555 rounded-[14px] text-left dark:border-dark-666">
                  <div className="p-4 w-full h-[40%] border-b-1 border-light-555 dark:border-dark-666">
                    <InfoItem isDark={isDark} className="text-left">
                      Activity
                    </InfoItem>
                    <div className="flex flex-col my-4 items-end">
                      <div className="p-4 bg-light-555 rounded-lg flex items-center justify-center text-light-0 text-[24px] font-bold dark:bg-dark-777 dark:text-white">
                        {activity}
                      </div>
                      <Progress
                        trailColor={isDark ? '#122A3B' : '#F2F2F2'}
                        strokeWidth={5}
                        strokeColor={isDark ? '#0f68aa' : '#0f68aa'}
                        percent={Number(activity.slice(0, -1))}
                        showInfo={false}
                      />
                    </div>
                  </div>
                  <div className="w-full h-[60%] grid grid-cols-2 grid-rows-[1fr,1fr]">
                    <div className="px-4 border-r-1 border-b-1 border-light-555 dark:border-dark-666">
                      <InfoItem isDark={isDark}>Self Stake</InfoItem>
                      <div>
                        <InfoData isDark={isDark}>{selfDeposit}</InfoData>
                        <span className="pl-2 text-light-444">WAN</span>
                      </div>
                    </div>
                    <div className="px-4 border-r-1 border-b-1 border-light-555 dark:border-dark-666">
                      <InfoItem isDark={isDark}>Delegation Fee (%)</InfoItem>
                      <InfoData isDark={isDark}>{delegateFee}</InfoData>
                    </div>
                    <div className="px-4 border-r-1 border-light-555 dark:border-dark-666">
                      <InfoItem isDark={isDark}>Rank</InfoItem>
                      <InfoData isDark={isDark}>
                        {rank[0] !== undefined &&
                        rank[0].toString() === '-1' ? (
                          <div>
                            <span>&gt;</span>
                            <span>{rank[1]}</span>
                          </div>
                        ) : (
                          <div>
                            <span>
                              {rank[0] === undefined ? '--' : rank[0]}
                            </span>{' '}
                            / <span>{rank[1]}</span>
                          </div>
                        )}
                      </InfoData>
                    </div>
                    <div className="px-4">
                      <InfoItem isDark={isDark}>Auto Renew</InfoItem>
                      <InfoData isDark={isDark}>
                        {!storemanStakeInfo?.[0]?.quited ? 'True' : 'False'}
                      </InfoData>
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={clsx(
                  'w-full flex justify-between mt-[20px] gap-4 md:flex-col',
                  {
                    'btn-light': !isDark,
                    'btn-dark': isDark,
                  },
                )}
              >
                <Button
                  disabled={isReady}
                  className="w-[32%] rounded-4xl h-[40px] btn text-[18px] md:w-full"
                  onClick={() => setShowTopUpModal(true)}
                >
                  Top-up
                </Button>
                <Button
                  disabled={isReady || !canExit}
                  className="w-[32%] rounded-4xl h-[40px] text-[18px] md:w-full"
                  onClick={() => setShowExitModal(true)}
                >
                  Exit
                </Button>
                <Button
                  className="w-[32%] rounded-4xl h-[40px] text-[18px] md:w-full"
                  onClick={() => setShowClaimModal(true)}
                >
                  Claim
                </Button>
              </div>
            </div>
          ) : (
            <EmptyData text="Sorry, you don’t have a bridge validator node yet." />
          )}
        </DataLoading>
      </div>
      {showTopUpModal && storemanStakeInfo && (
        <TopupModal
          vld_attr={vld_attr}
          visible={showTopUpModal}
          onCancel={() => setShowTopUpModal(false)}
          storemanStakeInfo={storemanStakeInfo[0]}
        />
      )}
      {showExitModal && storemanStakeInfo && (
        <ExitModal
          vld_attr={vld_attr}
          visible={showExitModal}
          onCancel={() => setShowExitModal(false)}
          storemanStakeInfo={storemanStakeInfo[0]}
        />
      )}
      {showClaimModal && storemanStakeInfo && (
        <ClaimModal
          vld_attr={vld_attr}
          visible={showClaimModal}
          unclaimedData={unclaimedData}
          onCancel={() => setShowClaimModal(false)}
          storemanStakeInfo={storemanStakeInfo[0]}
        />
      )}
    </StakeBridgeContainer>
  )
}

const Title = styled.div<{
  isDark: boolean
}>`
  font-size: 12px;
  color: ${({ isDark }) => (isDark ? '#fff' : '#333')};
  opacity: 0.5;
`

const Data = styled.span<{
  isDark: boolean
}>`
  font-size: 28px;
  color: ${({ isDark }) => (isDark ? '#fff' : '#000')};
`

const InfoItem = styled.div<{
  isDark: boolean
}>`
  margin-top: 1rem;
  color: ${({ isDark }) => (isDark ? '#fff' : '#333')};
  opacity: 0.5;
  font-size: 12px;
`

const InfoData = styled.div<{
  isDark: boolean
}>`
  color: ${({ isDark }) => (isDark ? '#fff' : '#333')};
  font-size: 24px;
  display: inline-block;
  font-weight: bold;
`
